<template>
  <div class="kyc-page">
    <nav class="kyc-nav"><div class="container d-flex align-items-center py-2">
      <a href="javascript:void(0)" @click="$router.go(-1)" class="kyc-back"><i class="mdi mdi-arrow-left"></i></a>
      <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
      <h6 class="mb-0" style="color:var(--fm-h,#F5F0E8);font-weight:600;">Seller Verification</h6>
    </div></nav>

    <div class="container kyc-container">
      <div class="row justify-content-center"><div class="col-lg-8">

        <!-- HEADER -->
        <div class="kyc-card mb-4 text-center">
          <div class="kyc-shield mb-3"><i class="mdi mdi-shield-check"></i></div>
          <h5 style="color:var(--fm-h,#F5F0E8);font-weight:700;">Get Verified</h5>
          <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0;">Verified sellers get a badge on their profile, appear higher in search, and buyers trust them more. It takes 24-48 hours to review.</p>
        </div>

        <!-- CURRENT STATUS -->
        <div class="kyc-card mb-4" v-if="verifications.length > 0">
          <h6 class="kyc-card-title"><i class="mdi mdi-clipboard-check me-2"></i>Your Verifications</h6>
          <div v-for="v in verifications" :key="v.id" class="kyc-verif-block">
            <div class="kyc-status-row">
              <div class="d-flex align-items-center gap-3">
                <i :class="'mdi '+verifIcon(v.verification_type)" style="font-size:22px;color:#BFA164;"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;">{{ verifLabel(v.verification_type) }}</div>
                  <div class="kyc-muted">{{ v.document_number || '' }} · Submitted {{ fD(v.created_at) }}</div>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
              <span class="kyc-badge" :class="'kyc-b-'+v.verification_status">
                <i v-if="v.verification_status==='verified'" class="mdi mdi-check-circle me-1"></i>
                <i v-else-if="v.verification_status==='pending'" class="mdi mdi-clock me-1"></i>
                <i v-else class="mdi mdi-close-circle me-1"></i>
                {{ v.verification_status }}
              </span>
              </div>
            </div>
            <!-- Rejection reason inline -->
            <div v-if="v.verification_status==='rejected' && v.rejection_reason" class="kyc-inline-msg kyc-msg-error">
              <i class="mdi mdi-alert-circle"></i>
              <div><strong>Rejected:</strong> {{ v.rejection_reason }}</div>
            </div>
            <!-- Admin messages for this verification -->
            <div v-for="n in getVerifMessages(v.id)" :key="n.id" class="kyc-inline-msg kyc-msg-info">
              <i class="mdi mdi-message-text"></i>
              <div>
                <div style="font-weight:600;">{{ n.title }}</div>
                <div>{{ n.body }}</div>
                <div class="kyc-muted" style="margin-top:2px;">{{ fDT(n.created_at) }}</div>
              </div>
            </div>
            <!-- Resubmit hint for rejected -->
            <div v-if="v.verification_status==='rejected'" class="kyc-inline-action">
              <button class="btn btn-sm btn-outline-warning" @click="form.type=v.verification_type"><i class="mdi mdi-refresh me-1"></i>Resubmit {{ verifLabel(v.verification_type) }}</button>
            </div>
          </div>
        </div>

        <!-- SUBMIT NEW VERIFICATION -->
        <div class="kyc-card mb-4">
          <h6 class="kyc-card-title"><i class="mdi mdi-file-document-edit me-2"></i>Submit Verification</h6>

          <!-- TYPE SELECTOR -->
          <div class="mb-3">
            <label class="kyc-label">Document Type</label>
            <div class="kyc-type-grid">
              <div v-for="t in verifTypes" :key="t.value" class="kyc-type-card" :class="{selected: form.type===t.value, disabled: isAlreadyVerified(t.value)}" @click="!isAlreadyVerified(t.value) ? form.type=t.value : null">
                <i :class="t.icon" class="kyc-type-icon" :style="{color: form.type===t.value ? '#BFA164' : 'var(--fm-s,#6B6356)'}"></i>
                <div class="kyc-type-name">{{ t.label }}</div>
                <div class="kyc-type-desc">{{ t.desc }}</div>
                <span v-if="isAlreadyVerified(t.value)" class="kyc-badge kyc-b-verified" style="font-size:9px;margin-top:4px;"><i class="mdi mdi-check me-1"></i>Done</span>
                <span v-else-if="isPendingVerif(t.value)" class="kyc-badge kyc-b-pending" style="font-size:9px;margin-top:4px;"><i class="mdi mdi-clock me-1"></i>Pending</span>
                <span v-else-if="isRejectedVerif(t.value)" class="kyc-badge kyc-b-rejected" style="font-size:9px;margin-top:4px;"><i class="mdi mdi-refresh me-1"></i>Resubmit</span>
              </div>
            </div>
          </div>

          <div v-if="form.type">
            <!-- DOCUMENT NUMBER -->
            <div class="mb-3">
              <label class="kyc-label">{{ docNumberLabel }}</label>
              <input v-model="form.documentNumber" type="text" class="kyc-input" :placeholder="docNumberPlaceholder" />
            </div>

            <!-- DOCUMENT FRONT -->
            <div class="mb-3">
              <label class="kyc-label">{{ form.type==='kra_pin' ? 'KRA PIN Certificate (PDF or Photo)' : form.type==='business_registration' ? 'Registration Certificate (PDF or Photo)' : 'Document Front Photo' }}</label>
              <div class="kyc-upload" :class="{filled: frontPreview || frontFileName}">
                <img v-if="frontPreview && !frontFileName" :src="frontPreview" class="kyc-upload-preview" @click="frontPreview=null;frontFile=null;frontFileName=''" title="Click to remove" />
                <!-- PDF preview -->
                <div v-else-if="frontFileName" class="kyc-pdf-preview" @click="frontPreview=null;frontFile=null;frontFileName=''">
                  <i class="mdi mdi-file-pdf-box" style="font-size:48px;color:#FF6B6B;"></i>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;margin-top:4px;">{{ frontFileName }}</div>
                  <div class="kyc-muted">Click to remove</div>
                </div>
                <div v-else class="kyc-upload-placeholder">
                  <i :class="form.type==='national_id' ? 'mdi mdi-image' : 'mdi mdi-file-document'" style="font-size:32px;color:var(--fm-s,#3A3530);"></i>
                  <div>{{ form.type==='kra_pin' ? 'Upload KRA PIN Certificate' : form.type==='business_registration' ? 'Upload Registration Certificate' : 'Front of document' }}</div>
                  <div class="kyc-upload-btns">
                    <button v-if="form.type==='national_id'" type="button" class="kyc-btn-cam" @click.stop="openCamera('front','environment')"><i class="mdi mdi-camera me-1"></i>Camera</button>
                    <button type="button" class="kyc-btn-file" @click.stop="$refs.frontFile.click()"><i :class="form.type==='national_id' ? 'mdi mdi-folder-image me-1' : 'mdi mdi-file-upload me-1'"></i>{{ form.type==='national_id' ? 'Gallery' : 'Upload File' }}</button>
                  </div>
                </div>
              </div>
              <input ref="frontFile" type="file" :accept="form.type==='national_id' ? 'image/*' : 'application/pdf,image/*'" style="display:none;" @change="onFileChange($event,'front')" />
            </div>

            <!-- DOCUMENT BACK (for ID) -->
            <div class="mb-3" v-if="form.type==='national_id'">
              <label class="kyc-label">Document Back Photo</label>
              <div class="kyc-upload" :class="{filled: backPreview}">
                <img v-if="backPreview" :src="backPreview" class="kyc-upload-preview" @click="backPreview=null;backFile=null" title="Click to remove" />
                <div v-else class="kyc-upload-placeholder">
                  <i class="mdi mdi-image" style="font-size:32px;color:var(--fm-s,#3A3530);"></i>
                  <div>Back of document</div>
                  <div class="kyc-upload-btns">
                    <button type="button" class="kyc-btn-cam" @click.stop="openCamera('back','environment')"><i class="mdi mdi-camera me-1"></i>Camera</button>
                    <button type="button" class="kyc-btn-file" @click.stop="$refs.backFile.click()"><i class="mdi mdi-folder-image me-1"></i>Gallery</button>
                  </div>
                </div>
              </div>
              <input ref="backFile" type="file" accept="image/*" style="display:none;" @change="onFileChange($event,'back')" />
            </div>

            <!-- SELFIE (not for KRA PIN - it's a PDF) -->
            <div class="mb-3" v-if="form.type==='national_id'">
              <label class="kyc-label">Selfie Holding Document</label>
              <div class="kyc-upload" :class="{filled: selfiePreview}">
                <img v-if="selfiePreview" :src="selfiePreview" class="kyc-upload-preview" @click="selfiePreview=null;selfieFile=null" title="Click to remove" />
                <div v-else class="kyc-upload-placeholder">
                  <i class="mdi mdi-account-box" style="font-size:32px;color:var(--fm-s,#3A3530);"></i>
                  <div>Selfie with document visible</div>
                  <div class="kyc-upload-btns">
                    <button type="button" class="kyc-btn-cam" @click.stop="openCamera('selfie','user')"><i class="mdi mdi-camera me-1"></i>Camera</button>
                    <button type="button" class="kyc-btn-file" @click.stop="$refs.selfieFile.click()"><i class="mdi mdi-folder-image me-1"></i>Gallery</button>
                  </div>
                </div>
              </div>
              <input ref="selfieFile" type="file" accept="image/*" style="display:none;" @change="onFileChange($event,'selfie')" />
            </div>

            <div v-if="submitError" style="color:#FF6B6B;font-size:12px;margin-bottom:10px;"><i class="mdi mdi-alert me-1"></i>{{ submitError }}</div>
            <div v-if="submitSuccess" style="color:#50C878;font-size:12px;margin-bottom:10px;"><i class="mdi mdi-check-circle me-1"></i>{{ submitSuccess }}</div>

            <button class="btn btn-warning text-dark fw-bold w-100" @click="submit" :disabled="submitting || !canSubmit">
              <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Submitting...</span>
              <span v-else><i class="mdi mdi-send me-1"></i>Submit for Review</span>
            </button>
          </div>
        </div>

        <!-- TIPS -->
        <div class="kyc-card">
          <h6 class="kyc-card-title"><i class="mdi mdi-lightbulb me-2" style="color:#FFAB00;"></i>Tips for Fast Approval</h6>
          <div class="kyc-tips">
            <div><i class="mdi mdi-check" style="color:#50C878;"></i> Photos must be clear, not blurry</div>
            <div><i class="mdi mdi-check" style="color:#50C878;"></i> All corners of the document must be visible</div>
            <div><i class="mdi mdi-check" style="color:#50C878;"></i> Selfie must show your face AND the document clearly</div>
            <div><i class="mdi mdi-check" style="color:#50C878;"></i> Document must not be expired</div>
            <div><i class="mdi mdi-check" style="color:#50C878;"></i> Name on document must match your profile name</div>
          </div>
        </div>

      </div></div>
    </div>

    <!-- LIVE CAMERA MODAL -->
    <div v-if="cameraOpen" class="kyc-camera-overlay">
      <div class="kyc-camera-modal">
        <div class="kyc-camera-header">
          <span style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ cameraTarget==='selfie' ? 'Take Selfie' : 'Capture Document' }}</span>
          <button @click="closeCamera" class="kyc-camera-close"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="kyc-camera-body">
          <video ref="cameraVideo" autoplay playsinline :class="{'kyc-mirror': cameraFacing==='user'}" v-show="!capturedImage"></video>
          <img v-if="capturedImage" :src="capturedImage" class="kyc-captured" />
          <canvas ref="cameraCanvas" style="display:none;"></canvas>
          <!-- Document guide overlay -->
          <div v-if="!capturedImage && cameraTarget!=='selfie'" class="kyc-doc-overlay">
            <div class="kyc-doc-frame">
              <div class="kyc-doc-corner kyc-tl"></div>
              <div class="kyc-doc-corner kyc-tr"></div>
              <div class="kyc-doc-corner kyc-bl"></div>
              <div class="kyc-doc-corner kyc-br"></div>
              <div class="kyc-doc-label"><i class="mdi mdi-card-account-details me-1"></i>{{ cameraTarget==='front' ? 'Front of ID' : 'Back of ID' }}</div>
            </div>
            <div class="kyc-doc-hint">Align your document within the frame</div>
          </div>
          <!-- Selfie guide overlay -->
          <div v-if="!capturedImage && cameraTarget==='selfie'" class="kyc-doc-overlay">
            <div class="kyc-selfie-frame"></div>
            <div class="kyc-doc-hint" style="bottom:12px;">Hold your document next to your face</div>
          </div>
        </div>
        <div class="kyc-camera-footer">
          <div v-if="cameraError" style="color:#FF6B6B;font-size:12px;text-align:center;margin-bottom:8px;"><i class="mdi mdi-alert me-1"></i>{{ cameraError }}</div>
          <div v-if="!capturedImage" class="d-flex align-items-center justify-content-center gap-3">
            <button class="kyc-btn-switch" @click="switchCamera" title="Switch camera"><i class="mdi mdi-camera-flip"></i></button>
            <button class="kyc-btn-capture" @click="capturePhoto"><i class="mdi mdi-camera"></i></button>
            <button class="kyc-btn-switch" @click="closeCamera" title="Cancel" style="background:rgba(255,107,107,0.15);"><i class="mdi mdi-close" style="color:#FF6B6B;"></i></button>
          </div>
          <div v-else class="d-flex gap-2 w-100">
            <button class="kyc-btn-file flex-fill" @click="capturedImage=null"><i class="mdi mdi-refresh me-1"></i>Retake</button>
            <button class="kyc-btn-cam flex-fill" @click="usePhoto"><i class="mdi mdi-check me-1"></i>Use Photo</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, flocker, uploads } from "@/api";
export default {
  name: "SellerVerificationPage",
  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',

      loading: true, verifications: [], verifNotifications: [],
      form: { type: '', documentNumber: '' },
      frontFile: null, backFile: null, selfieFile: null,
      frontPreview: null, backPreview: null, selfiePreview: null,
      frontFileName: '',
      submitting: false, submitError: '', submitSuccess: '',
      // Camera
      cameraOpen: false, cameraTarget: '', cameraFacing: 'environment', cameraStream: null, capturedImage: null, cameraError: '',
      verifTypes: [
        { value: 'national_id', label: 'National ID', desc: 'Kenyan ID card', icon: 'mdi mdi-card-account-details' },
        { value: 'kra_pin', label: 'KRA PIN', desc: 'Tax compliance cert', icon: 'mdi mdi-file-certificate' },
        { value: 'business_registration', label: 'Business Reg', desc: 'Certificate of registration', icon: 'mdi mdi-domain' },
      ]
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    canSubmit() { return this.form.type && this.form.documentNumber && this.frontFile; },
    docNumberLabel() {
      if (this.form.type === 'national_id') return 'ID Number';
      if (this.form.type === 'kra_pin') return 'KRA PIN';
      if (this.form.type === 'business_registration') return 'Registration Number';
      return 'Document Number';
    },
    docNumberPlaceholder() {
      if (this.form.type === 'national_id') return 'e.g. 12345678';
      if (this.form.type === 'kra_pin') return 'e.g. A012345678B';
      if (this.form.type === 'business_registration') return 'e.g. PVT-ABC1234';
      return '';
    }
  },
  mounted() {
    // Apply global theme
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);

    document.title = 'FLOCKR | Verification';
    this.loadVerifications();
  },
  beforeUnmount() {
    if (this.cameraStream) { this.cameraStream.getTracks().forEach(function(t){ t.stop(); }); }
  },
  methods: {
    fD(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' }) : '-'; },
    fDT(d) { return d ? new Date(d).toLocaleString('en-KE', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-'; },
    getVerifMessages(verifId) {
      var id = String(verifId);
      return this.verifNotifications.filter(function(n) { return String(n.reference_id) === id; });
    },
    verifLabel(t) {
      var map = { national_id:'National ID', kra_pin:'KRA PIN', business_registration:'Business Registration', kvb_license:'Vet License', phone_otp:'Phone', email:'Email', location_gps:'Location' };
      return map[t] || t;
    },
    verifIcon(t) {
      var map = { national_id:'mdi-card-account-details', kra_pin:'mdi-file-certificate', business_registration:'mdi-domain', phone_otp:'mdi-phone-check', email:'mdi-email-check', location_gps:'mdi-crosshairs-gps' };
      return map[t] || 'mdi-file-document';
    },
    isAlreadyVerified(type) {
      return this.verifications.some(function(v) { return v.verification_type === type && v.verification_status === 'verified'; });
    },
    isPendingVerif(type) {
      return this.verifications.some(function(v) { return v.verification_type === type && v.verification_status === 'pending'; });
    },
    isRejectedVerif(type) {
      return this.verifications.some(function(v) { return v.verification_type === type && v.verification_status === 'rejected'; });
    },
    onFileChange(e, which) {
      var file = e.target.files[0]; if (!file) return;
      var self = this;
      var isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        // PDF - just store file and show name
        if (which === 'front') { self.frontFile = file; self.frontPreview = null; self.frontFileName = file.name; }
        return;
      }

      // Image - read and show preview
      var reader = new FileReader();
      reader.onload = function(ev) {
        if (which === 'front') { self.frontFile = file; self.frontPreview = ev.target.result; self.frontFileName = ''; }
        if (which === 'back') { self.backFile = file; self.backPreview = ev.target.result; }
        if (which === 'selfie') { self.selfieFile = file; self.selfiePreview = ev.target.result; }
      };
      reader.readAsDataURL(file);
    },
    async loadVerifications() {
      this.loading = true;
      try {
        var f = new FormData(); f.append('function', 'getUserProfile'); f.append('userId', sessionStorage.getItem('userId'));
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.user) {
          this.verifications = r.data.data.user.verifications || [];
        }
      } catch (e) {}
      // Load verification-related notifications
      try {
        var f2 = new FormData(); f2.append('function', 'getNotifications'); f2.append('userId', sessionStorage.getItem('userId')); f2.append('referenceType', 'verification');
        var r2 = await execute(f2, flocker);
        if (r2.data && r2.data.success && r2.data.data) {
          this.verifNotifications = r2.data.data.notifications || [];
        }
      } catch (e) {}
      this.loading = false;
    },
    async submit() {
      this.submitError = ''; this.submitSuccess = '';
      if (!this.form.type || !this.form.documentNumber) { this.submitError = 'Please select a type and enter the document number.'; return; }
      if (!this.frontFile) { this.submitError = 'Please upload the front of your document.'; return; }
      this.submitting = true;
      try {
        var f = new FormData();
        f.append('function', 'submitVerification');
        f.append('userId', sessionStorage.getItem('userId'));
        f.append('verificationType', this.form.type);
        f.append('documentNumber', this.form.documentNumber);
        f.append('documentFront', this.frontFile);
        if (this.backFile) f.append('documentBack', this.backFile);
        if (this.selfieFile) f.append('selfie', this.selfieFile);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.submitSuccess = 'Verification submitted! We will review within 24-48 hours.';
          this.form = { type: '', documentNumber: '' };
          this.frontFile = null; this.backFile = null; this.selfieFile = null;
          this.frontPreview = null; this.backPreview = null; this.selfiePreview = null;
          this.frontFileName = '';
          this.loadVerifications();
        } else { this.submitError = (r.data && r.data.message) || 'Submission failed.'; }
      } catch (e) { this.submitError = 'Network error.'; }
      this.submitting = false;
    },

    // ====== CAMERA ======
    async openCamera(target, facing) {
      this.cameraTarget = target;
      this.cameraFacing = facing || 'environment';
      this.capturedImage = null;
      this.cameraError = '';
      this.cameraOpen = true;
      await this.$nextTick();
      this.startStream();
    },
    async startStream() {
      try {
        if (this.cameraStream) { this.cameraStream.getTracks().forEach(function(t){ t.stop(); }); }
        var constraints = { video: { facingMode: this.cameraFacing, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false };
        this.cameraStream = await navigator.mediaDevices.getUserMedia(constraints);
        if (this.$refs.cameraVideo) { this.$refs.cameraVideo.srcObject = this.cameraStream; }
        this.cameraError = '';
      } catch (e) {
        this.cameraError = 'Could not access camera. Please allow camera permission or use Gallery instead.';
      }
    },
    capturePhoto() {
      var video = this.$refs.cameraVideo;
      var canvas = this.$refs.cameraCanvas;
      if (!video || !canvas) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      var ctx = canvas.getContext('2d');
      if (this.cameraFacing === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0);
      this.capturedImage = canvas.toDataURL('image/jpeg', 0.85);
    },
    usePhoto() {
      if (!this.capturedImage) return;
      var self = this;
      // Convert dataURL to File
      fetch(this.capturedImage).then(function(res) { return res.blob(); }).then(function(blob) {
        var file = new File([blob], 'camera_' + self.cameraTarget + '_' + Date.now() + '.jpg', { type: 'image/jpeg' });
        if (self.cameraTarget === 'front') { self.frontFile = file; self.frontPreview = self.capturedImage; }
        if (self.cameraTarget === 'back') { self.backFile = file; self.backPreview = self.capturedImage; }
        if (self.cameraTarget === 'selfie') { self.selfieFile = file; self.selfiePreview = self.capturedImage; }
        self.closeCamera();
      });
    },
    async switchCamera() {
      this.cameraFacing = this.cameraFacing === 'user' ? 'environment' : 'user';
      this.capturedImage = null;
      await this.startStream();
    },
    closeCamera() {
      if (this.cameraStream) { this.cameraStream.getTracks().forEach(function(t){ t.stop(); }); this.cameraStream = null; }
      this.cameraOpen = false; this.capturedImage = null;
    }
  }
};
</script>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
html[data-theme="light"] .kyc-page{background:#F7F5F2!important}
html[data-theme="light"] .kyc-nav{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .kyc-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .kyc-type-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .kyc-type-card:hover:not(.disabled){border-color:#D9D0C5!important}
html[data-theme="light"] .kyc-type-card.selected{background:rgba(191,161,100,0.06)!important;border-color:#BFA164!important}
html[data-theme="light"] .kyc-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .kyc-input::placeholder{color:#BFBAB3!important}
html[data-theme="light"] .kyc-upload{border-color:#D9D0C5!important}
html[data-theme="light"] .kyc-upload:hover{border-color:#BFA164!important}
html[data-theme="light"] .kyc-camera-modal{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .kyc-camera-header{border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .kyc-camera-footer{border-top-color:#E8E2DA!important}
html[data-theme="light"] .kyc-input-addon{background:#F7F5F2!important;border-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#1A1A1A"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="background:#2A2520"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0F200F"]{background:#EDF7EF!important}
html[data-theme="light"] [style*="background:#200F0F"]{background:#FFF2F2!important}
html[data-theme="light"] [style*="background:#200F20"]{background:#F8F0F8!important}
html[data-theme="light"] [style*="background:#0F2030"]{background:#E8F4FD!important}
html[data-theme="light"] [style*="border:1px solid #1A1A1A"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border-top:1px solid #1E1E1E"]{border-top-color:#E8E2DA!important}
</style>
<style scoped>
.kyc-page { min-height:100vh; background:var(--fm-bg2,#0A0A0A); padding-bottom:60px; }
.kyc-nav { background:var(--fm-bg2,#0A0A0A); border-bottom:1px solid var(--fm-br,#1E1E1E); position:fixed; top:0; left:0; right:0; z-index:100; }
.kyc-back { color:var(--fm-m,#A09888); font-size:22px; margin-right:12px; text-decoration:none; }
.kyc-container { padding-top:80px; max-width:800px; }
.kyc-card { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; padding:24px; }
.kyc-card-title { color:#BFA164; font-size:14px; font-weight:700; margin-bottom:16px; }
.kyc-label { display:block; color:var(--fm-m,#A09888); font-size:12px; font-weight:500; margin-bottom:6px; }
.kyc-input { width:100%; background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; padding:10px 14px; color:var(--fm-h,#F5F0E8); font-size:14px; }
.kyc-input:focus { border-color:#BFA164; outline:none; }
.kyc-input::placeholder { color:var(--fm-s,#3A3530); }
.kyc-muted { color:var(--fm-s,#6B6356); font-size:11px; }
.kyc-shield { width:56px; height:56px; border-radius:50%; background:rgba(80,200,120,0.12); display:flex; align-items:center; justify-content:center; margin:0 auto; }
.kyc-shield i { font-size:28px; color:#50C878; }

.kyc-type-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; }
.kyc-type-card { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:14px; text-align:center; cursor:pointer; transition:all .2s; }
.kyc-type-card:hover:not(.disabled) { border-color:var(--fm-br,#2A2520); }
.kyc-type-card.selected { border-color:#BFA164; background:rgba(191,161,100,0.06); }
.kyc-type-card.disabled { opacity:0.5; cursor:not-allowed; }
.kyc-type-icon { font-size:28px; margin-bottom:6px; }
.kyc-type-name { color:var(--fm-h,#F5F0E8); font-size:13px; font-weight:600; }
.kyc-type-desc { color:var(--fm-s,#6B6356); font-size:10px; }

.kyc-upload { border:2px dashed var(--fm-br,#2A2520); border-radius:10px; padding:20px; text-align:center; cursor:pointer; transition:all .2s; min-height:120px; display:flex; align-items:center; justify-content:center; overflow:hidden; }
.kyc-upload:hover { border-color:#BFA164; }
.kyc-upload.filled { padding:0; border-style:solid; border-color:#50C878; }
.kyc-upload-preview { width:100%; height:auto; max-height:200px; object-fit:contain; border-radius:8px; cursor:pointer; }
.kyc-pdf-preview { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; cursor:pointer; width:100%; }
.kyc-upload-placeholder { color:#4A4540; font-size:12px; }
.kyc-upload-btns { display:flex; gap:8px; margin-top:10px; }
.kyc-btn-cam, .kyc-btn-file { padding:6px 14px; border-radius:8px; font-size:12px; font-weight:600; border:none; cursor:pointer; display:flex; align-items:center; transition:all .2s; }
.kyc-btn-cam { background:rgba(191,161,100,0.15); color:#BFA164; }
.kyc-btn-cam:hover { background:rgba(191,161,100,0.25); }
.kyc-btn-file { background:rgba(107,99,86,0.15); color:var(--fm-m,#A09888); }
.kyc-btn-file:hover { background:rgba(107,99,86,0.25); }

.kyc-status-row { display:flex; align-items:center; justify-content:space-between; padding:12px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); }
.kyc-status-row:last-child { border-bottom:none; }
.kyc-verif-block { border-bottom:1px solid var(--fm-br,#1E1E1E); padding-bottom:4px; margin-bottom:4px; }
.kyc-verif-block:last-child { border-bottom:none; margin-bottom:0; padding-bottom:0; }
.kyc-verif-block .kyc-status-row { border-bottom:none; padding-bottom:6px; }
.kyc-inline-msg { display:flex; gap:10px; padding:10px 14px; border-radius:8px; font-size:12px; line-height:1.5; margin:0 0 8px 34px; }
.kyc-msg-error { background:rgba(255,107,107,0.06); border:1px solid rgba(255,107,107,0.15); color:#FF6B6B; }
.kyc-msg-error i { font-size:16px; flex-shrink:0; margin-top:1px; }
.kyc-msg-info { background:rgba(191,161,100,0.06); border:1px solid rgba(191,161,100,0.15); color:var(--fm-m,#A09888); }
.kyc-msg-info i { font-size:16px; color:#BFA164; flex-shrink:0; margin-top:1px; }
.kyc-msg-info strong { color:var(--fm-h,#F5F0E8); }
.kyc-inline-action { margin:0 0 10px 34px; }
.kyc-badge { display:inline-flex; align-items:center; padding:3px 10px; border-radius:5px; font-size:11px; font-weight:600; text-transform:capitalize; }
.kyc-b-verified { background:rgba(80,200,120,0.1); color:#50C878; }
.kyc-b-pending { background:rgba(255,171,0,0.1); color:#FFAB00; }
.kyc-b-rejected { background:rgba(255,107,107,0.1); color:#FF6B6B; }
.kyc-b-expired { background:rgba(107,99,86,0.15); color:var(--fm-s,#6B6356); }
.kyc-rejection { background:rgba(255,107,107,0.06); border:1px solid rgba(255,107,107,0.15); border-radius:8px; padding:10px 12px; font-size:12px; color:#FF6B6B; }
.kyc-tips { display:flex; flex-direction:column; gap:8px; }
.kyc-tips > div { font-size:13px; color:var(--fm-m,#A09888); display:flex; align-items:center; gap:8px; }

@media (max-width:768px) {
  .kyc-type-grid { grid-template-columns:1fr; }
}

/* Camera modal */
.kyc-camera-overlay { position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.85); z-index:9999; display:flex; align-items:center; justify-content:center; padding:20px; }
.kyc-camera-modal { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520); border-radius:16px; overflow:hidden; width:100%; max-width:560px; }
.kyc-camera-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid var(--fm-br,#1E1E1E); }
.kyc-camera-close { background:none; border:none; color:var(--fm-s,#6B6356); font-size:22px; cursor:pointer; padding:0; }
.kyc-camera-close:hover { color:#FF6B6B; }
.kyc-camera-body { position:relative; background:#000; }
.kyc-camera-body video { width:100%; display:block; max-height:400px; object-fit:cover; }
.kyc-camera-body .kyc-captured { width:100%; display:block; max-height:400px; object-fit:contain; }
.kyc-mirror { transform:scaleX(-1); }

/* Document guide overlay */
.kyc-doc-overlay { position:absolute; top:0; left:0; right:0; bottom:0; pointer-events:none; display:flex; flex-direction:column; align-items:center; justify-content:center; }
.kyc-doc-frame { position:relative; width:82%; aspect-ratio:1.586/1; border:2px solid rgba(191,161,100,0.6); border-radius:12px; box-shadow:0 0 0 9999px rgba(0,0,0,0.45); }
.kyc-doc-corner { position:absolute; width:24px; height:24px; border-color:#BFA164; border-style:solid; }
.kyc-tl { top:-2px; left:-2px; border-width:3px 0 0 3px; border-radius:8px 0 0 0; }
.kyc-tr { top:-2px; right:-2px; border-width:3px 3px 0 0; border-radius:0 8px 0 0; }
.kyc-bl { bottom:-2px; left:-2px; border-width:0 0 3px 3px; border-radius:0 0 0 8px; }
.kyc-br { bottom:-2px; right:-2px; border-width:0 3px 3px 0; border-radius:0 0 8px 0; }
.kyc-doc-label { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); color:rgba(191,161,100,0.7); font-size:13px; font-weight:600; white-space:nowrap; display:flex; align-items:center; text-shadow:0 1px 4px rgba(0,0,0,0.6); }
.kyc-doc-hint { color:rgba(245,240,232,0.7); font-size:12px; margin-top:12px; text-shadow:0 1px 4px rgba(0,0,0,0.8); text-align:center; }

/* Selfie guide */
.kyc-selfie-frame { width:55%; aspect-ratio:3/4; border:2px dashed rgba(191,161,100,0.5); border-radius:50% 50% 44% 44%; box-shadow:0 0 0 9999px rgba(0,0,0,0.4); }
.kyc-camera-footer { padding:16px 18px; border-top:1px solid var(--fm-br,#1E1E1E); }
.kyc-btn-capture { width:64px; height:64px; border-radius:50%; background:rgba(191,161,100,0.2); border:3px solid #BFA164; color:#BFA164; font-size:24px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.kyc-btn-capture:hover { background:#BFA164; color:#0A0A0A; }
.kyc-btn-switch { width:42px; height:42px; border-radius:50%; background:rgba(107,99,86,0.15); border:none; color:var(--fm-m,#A09888); font-size:18px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.kyc-btn-switch:hover { background:rgba(107,99,86,0.3); }
</style>